import { useState } from 'react';
import { sanitizeOutcomeDisplay } from '@/i18n/outcome-labels';
import { cn } from '@/lib/cn';
import type { EventOutcome } from './EventRow';
import { Icon } from '../common/icon';
import { OddsButton } from '../common/odds-button';

export interface MarketCardLine {
  specifier?: string;
  main: boolean;
  outcomes: EventOutcome[];
}

export interface MarketCardProps {
  title: string;
  lines: MarketCardLine[];
  defaultExpanded?: boolean;
  alternativeLabel?: string;
  onSelectOutcome?: (outcome: EventOutcome) => void;
  className?: string;
}

/** Collapsible market card from the Figma Event Detail frame. */
export function MarketCard({
  title,
  lines,
  defaultExpanded = true,
  alternativeLabel = 'Alternative Options',
  onSelectOutcome,
  className,
}: MarketCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const mainLine = lines.find((line) => line.main) ?? lines[0];
  const alternativeLines = lines.filter((line) => line !== mainLine);

  return (
    <article className={cn('flex flex-col gap-3 rounded-md bg-bg-surface p-3', className)}>
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="min-w-0 truncate text-body-m font-bold text-text-primary">{title}</span>
        <Icon
          name="arrow-down"
          size={12}
          className={cn('shrink-0 text-icon-muted transition-transform', !expanded && '-rotate-180')}
        />
      </button>

      {expanded ? (
        <div className="flex flex-col gap-2">
          {mainLine ? <OutcomeRow outcomes={mainLine.outcomes} onSelectOutcome={onSelectOutcome} /> : null}
          {alternativeLines.length > 0 ? (
            <>
              <p className="text-caption text-text-secondary">{alternativeLabel}</p>
              {alternativeLines.map((line) => (
                <OutcomeRow
                  key={line.specifier ?? line.outcomes.map((outcome) => outcome.id).join(',')}
                  outcomes={line.outcomes}
                  onSelectOutcome={onSelectOutcome}
                />
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function OutcomeRow({
  outcomes,
  onSelectOutcome,
}: {
  outcomes: EventOutcome[];
  onSelectOutcome?: (outcome: EventOutcome) => void;
}) {
  // 1–3 outcomes stay on one equal row (1X2 / BTTS). Denser markets wrap so each
  // button can size to its label + price instead of collapsing into clipped slivers.
  const wrap = outcomes.length > 3;

  return (
    <div
      className={cn(
        'gap-2',
        wrap ? 'grid grid-cols-2 sm:grid-cols-3' : 'flex items-stretch',
      )}
    >
      {outcomes.map((outcome) => (
        <OddsButton
          key={outcome.id}
          outcome={sanitizeOutcomeDisplay(outcome.outcome)}
          odds={outcome.odds}
          state={outcome.state ?? 'default'}
          flash={outcome.flash ?? 'none'}
          className={wrap ? 'min-w-0 w-full' : undefined}
          onClick={() => onSelectOutcome?.(outcome)}
        />
      ))}
    </div>
  );
}
